import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useApp } from "../../../context/AppContextProvider";
import { useAuth } from "../../../context/UserContextProvider";
import { retrieveLearnerMainLeftNavigationOptionList } from "../../../utilization/HeaderUtilization";
import { checkViewPermissionGranted } from "../../../utilization/ScreenUtilization";
import { isStaffSentPersonalizedAssignment } from "../../../utilization/PersonalizedAssignmentTriggerUtilization";
import i18n from "../../../language/i18n";
import { NotificationManager } from "../../../notifications/NotificationManager";
import {
  Icon,
  iconExists,
  DividerLine,
  FullCard,
  BaseTitle,
} from "../../../library";
import { mainLeftNavigationStyles } from "./MainLeftNavigationStyles";
import MascotWithItems from "../mascots/MascotWithItems";
import TourLauncherPanel from "../tour/TourLauncherPanel";

const MainLeftNavigation = () => {
  const navigate = useNavigate();
  const { setBoarding, tourForceNavExpanded } = useApp();
  const {
    userInformation,
    userProfile,
    handleStateChangeProfile,
    handleStateLogout,
    activeContentList,
    activePersonalizedAssignmentList,
    contestDTOList,
    numberOfUnreadAnnouncements,
    myWrongActivityDTOList,
    activityReviewDTOList,
    webSocketConnectionStatus,
    mascotInventoryItemDTOList,
    userProfileGrade,
    userProfileBranch,
  } = useAuth();
  const [expanded, setExpanded] = useState(false);
  const [selectedParentOptionId, setSelectedParentOptionId] = useState<
    string | null
  >(null);
  const [selectedSubOptionId, setSelectedSubOptionId] = useState<string | null>(
    null,
  );
  const [showTourPanel, setShowTourPanel] = useState(false);
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const navigationScrollRef = useRef<HTMLDivElement | null>(null);
  const profileMenuRef = useRef<HTMLDivElement | null>(null);
  // During a guided tour the engine pins the nav open so its items stay anchored.
  const navExpanded = expanded || !!tourForceNavExpanded;

  useEffect(() => {
    const pathName = window.location.pathname;
    const foundParentNavigationOption =
      visibleMainLeftNavigationOptionList.find(
        (navigationOption: any) => navigationOption.navigate === pathName,
      );

    if (foundParentNavigationOption) {
      setSelectedParentOptionId(foundParentNavigationOption.id);
      setSelectedSubOptionId(null);
    } else {
      const foundSubNavigationOption = visibleMainLeftNavigationOptionList
        .filter(
          (navigationOption: any) => navigationOption.subOptionList !== null,
        )
        .find((navigationOption: any) =>
          navigationOption.subOptionList.find(
            (subNavigationOption: any) =>
              subNavigationOption.navigate === pathName,
          ),
        );
      if (foundSubNavigationOption) {
        setSelectedParentOptionId(foundSubNavigationOption.id);
        const subOptionList = foundSubNavigationOption.subOptionList;
        const foundSubOption = subOptionList.find(
          (subNavigationOption: any) =>
            subNavigationOption.navigate === pathName,
        );
        setSelectedSubOptionId(foundSubOption.id);
      } else {
        setSelectedParentOptionId(null);
        setSelectedSubOptionId(null);
      }
    }
  }, [window.location.pathname]);

  // Close the profile menu on outside-click / Escape — it is a popover anchored to
  // the avatar trigger in the footer.
  useEffect(() => {
    if (!showProfileMenu) return;
    const onPointerDown = (e: MouseEvent) => {
      if (
        profileMenuRef.current &&
        !profileMenuRef.current.contains(e.target as Node)
      ) {
        setShowProfileMenu(false);
      }
    };
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") setShowProfileMenu(false);
    };
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [showProfileMenu]);

  const calculateHomeworkNotificationCount = () => {
    return activeContentList.filter(
      (item: any) => item.completionStatus !== "COMPLETED",
    ).length;
  };

  const calculatePersonalizedBranchAssignmentCount = () => {
    // Both staff triggers land in Ödevlerim, so both count into its badge.
    return activePersonalizedAssignmentList.filter(
      (item: any) =>
        isStaffSentPersonalizedAssignment(item.triggerType) &&
        item.completionStatus !== "COMPLETED",
    ).length;
  };

  const calculateDashboardNotificationCount = () => {
    return activeContentList.filter(
      (item: any) => item.completionStatus !== "COMPLETED",
    ).length;
  };

  const calculateContestNotificationCount = () => {
    const contestCoterieTypeList = ["GENERAL_CULTURE", "TURKISH"];
    const filteredContestList = contestDTOList.filter(
      (contest: any) =>
        contestCoterieTypeList.includes(contest.coterieType) &&
        contest.isEvaluated,
    );
    return contestCoterieTypeList.length - filteredContestList.length;
  };

  const calculatePractiseNowNotificationCount = () => {
    return myWrongActivityDTOList.length - activityReviewDTOList.length;
  };

  const filterVisibleMainLeftNavigationOptionList = (
    mainLeftNavigationOptionList: any[],
  ) => {
    return mainLeftNavigationOptionList.filter((option: any) => option.visible);
  };

  const filterMainLeftNavigationOptionList = (
    visibleMainLeftNavigationOptionList: any[],
  ) => {
    if (userProfile && userProfile.userDTO) {
      if (userProfile.userDTO.accountType === "GENIXO") {
        return visibleMainLeftNavigationOptionList;
      } else {
        const filteredMainLeftNavigationOptionList: any[] = [];
        visibleMainLeftNavigationOptionList.forEach((navigationOption: any) => {
          if (navigationOption.subOptionList) {
            if (
              navigationOption.roleList.findIndex(
                (role: string) => role === userProfile.role,
              ) > -1
            ) {
              const filteredSubOptionList: any[] = [];
              navigationOption.subOptionList.forEach(
                (subNavigationOption: any) => {
                  if (
                    subNavigationOption.roleList.findIndex(
                      (role: string) => role === userProfile.role,
                    ) > -1
                  ) {
                    if (
                      checkViewPermissionGranted(
                        subNavigationOption.authorityScreenName,
                      )
                    ) {
                      filteredSubOptionList.push(subNavigationOption);
                    }
                  }
                },
              );
              if (filteredSubOptionList.length > 0) {
                navigationOption.subOptionList = filteredSubOptionList;
                filteredMainLeftNavigationOptionList.push(navigationOption);
              }
            }
          } else {
            if (
              navigationOption.roleList.findIndex(
                (role: string) => role === userProfile.role,
              ) > -1
            ) {
              if (navigationOption.authorityScreenName === "dashboard") {
                filteredMainLeftNavigationOptionList.push(navigationOption);
              } else {
                if (
                  checkViewPermissionGranted(
                    navigationOption.authorityScreenName,
                  )
                ) {
                  filteredMainLeftNavigationOptionList.push(navigationOption);
                }
              }
            }
          }
        });
        return filteredMainLeftNavigationOptionList;
      }
    }
    return [];
  };

  let mainLeftNavigationOptionList: any[] = [];
  if (userProfile?.role === "LEARNER") {
    mainLeftNavigationOptionList =
      retrieveLearnerMainLeftNavigationOptionList();
  }

  const visibleMainLeftNavigationOptionList =
    filterVisibleMainLeftNavigationOptionList(mainLeftNavigationOptionList);
  const filteredMainLeftNavigationOptionList =
    filterMainLeftNavigationOptionList(visibleMainLeftNavigationOptionList);
  const dashboardNotificationCount = calculateDashboardNotificationCount();
  const homeworkNotificationCount = calculateHomeworkNotificationCount();
  const personalizedBranchAssignmentCount =
    calculatePersonalizedBranchAssignmentCount();
  const contestNoficationCount = calculateContestNotificationCount();
  const practiseNowNotificationCount = calculatePractiseNowNotificationCount();

  return (
    <>
      <style>{mainLeftNavigationStyles}</style>
      <TourLauncherPanel
        isOpen={showTourPanel}
        onRequestClose={() => setShowTourPanel(false)}
      />
      <div
        className="qba-root"
        onMouseEnter={() => setExpanded(true)}
        onMouseLeave={() => setExpanded(false)}
        onFocusCapture={() => setExpanded(true)}
        onBlurCapture={(e) => {
          if (!e.currentTarget.contains(e.relatedTarget as Node)) {
            setExpanded(false);
          }
        }}
      >
        <div
          className={`qba-panel ${
            navExpanded ? "qba-panel-full" : "qba-panel-icon-only"
          }`}
        >
          <div className="qba-inner">
            <div className="qba-logo-wrapper">
              {navExpanded ? (
                <img
                  src="/logos/logo-primary.webp"
                  alt="main-logo"
                  title={i18n.t("dashboard")}
                  style={{ objectFit: "contain", cursor: "pointer" }}
                  onClick={() => {
                    navigate("/");
                  }}
                />
              ) : (
                <img
                  src="/logos/logo-primary-icon.webp"
                  alt="main-logo-icon"
                  className="qba-logo-icon"
                  title={i18n.t("dashboard")}
                  style={{ objectFit: "contain", cursor: "pointer" }}
                  onClick={() => {
                    navigate("/");
                  }}
                />
              )}
            </div>
            <div className="qba-scroll no-scrollbar" ref={navigationScrollRef}>
              <div className="qba-option-list">
                {filteredMainLeftNavigationOptionList.map(
                  (navigationOption: any) => (
                    <div
                      key={navigationOption.id}
                      className="qba-option"
                      data-parent-option-id={navigationOption.id}
                      data-tour={`nav-${navigationOption.id}`}
                    >
                      <div
                        className={`qba-option-header ${
                          navExpanded
                            ? "qba-option-header-full"
                            : "qba-option-header-icon-only"
                        } ${
                          selectedParentOptionId === navigationOption.id
                            ? "qba-option-header-selected"
                            : "qba-option-header-unselected"
                        }`}
                        title={navigationOption.name}
                        onClick={() => {
                          if (navigationOption.navigate) {
                            // Leaf nav item: always select + navigate. Never toggle
                            // off — re-clicking the current page must keep it selected.
                            setSelectedParentOptionId(navigationOption.id);
                            setSelectedSubOptionId(null);
                            navigate(navigationOption.navigate);
                          } else if (
                            selectedParentOptionId === navigationOption.id
                          ) {
                            // Accordion parent: re-click collapses the sub-option list.
                            setSelectedParentOptionId(null);
                          } else {
                            setSelectedParentOptionId(navigationOption.id);
                          }
                        }}
                      >
                        <div className="qba-option-icon-group">
                          <div className="qba-icon-wrapper">
                            {navigationOption.id === "homeworks" &&
                            (homeworkNotificationCount > 0 ||
                              personalizedBranchAssignmentCount > 0) ? (
                              <div className="qba-pill-count">
                                <span className="qba-pill-count-text">
                                  {homeworkNotificationCount}
                                </span>
                              </div>
                            ) : (navigationOption.id === "dashboard" &&
                                dashboardNotificationCount > 0) ||
                              (navigationOption.id === "contests" &&
                                contestNoficationCount > 0) ||
                              (navigationOption.id === "practises" &&
                                practiseNowNotificationCount > 0) ||
                              (navigationOption.id === "announcements" &&
                                numberOfUnreadAnnouncements > 0) ? (
                              <div className="qba-pill-dot" />
                            ) : null}

                            {(() => {
                              const navIcon =
                                selectedParentOptionId === navigationOption.id
                                  ? navigationOption.selectedIcon
                                  : navigationOption.icon;
                              return iconExists(navIcon) ? (
                                <Icon
                                  name={navIcon}
                                  size={20}
                                  color="currentColor"
                                />
                              ) : navIcon ? (
                                <img
                                  src={navIcon}
                                  alt="navigation-icon"
                                  style={{
                                    width: 20,
                                    height: 20,
                                    objectFit: "contain",
                                  }}
                                />
                              ) : null;
                            })()}
                          </div>
                          {navExpanded ? (
                            <div className="qba-option-label">
                              <span
                                className={`${
                                  selectedParentOptionId === navigationOption.id
                                    ? "qba-option-label-selected"
                                    : null
                                }`}
                              >
                                {navigationOption.name}
                              </span>
                            </div>
                          ) : null}
                        </div>
                        {navExpanded && navigationOption.subOptionList ? (
                          <div className="">
                            <Icon
                              name={
                                selectedParentOptionId === navigationOption.id
                                  ? "header-arrow-up-selected"
                                  : "header-arrow-down"
                              }
                              className="qba-arrow"
                            />
                          </div>
                        ) : null}
                      </div>
                      {navExpanded &&
                      navigationOption.subOptionList &&
                      selectedParentOptionId === navigationOption.id ? (
                        <div className="qba-suboption-list">
                          {navigationOption.subOptionList.map(
                            (subNavigationOption: any) => (
                              <div
                                key={subNavigationOption.id}
                                className="qba-suboption"
                                onClick={() => {
                                  setSelectedSubOptionId(
                                    subNavigationOption.id,
                                  );
                                  navigate(subNavigationOption.navigate);
                                }}
                              >
                                <p className={`qba-suboption-text`}>
                                  <span
                                    className={`${
                                      selectedSubOptionId ===
                                      subNavigationOption.id
                                        ? "qba-suboption-label-selected"
                                        : "qba-suboption-label-unselected"
                                    } qba-suboption-label`}
                                  >
                                    {subNavigationOption.name}
                                  </span>
                                </p>
                              </div>
                            ),
                          )}
                        </div>
                      ) : null}
                    </div>
                  ),
                )}
              </div>
            </div>
            <div className="qba-footer">
              {userProfile?.role === "LEARNER" ? (
                <div
                  className={`qba-faq-button ${
                    navExpanded
                      ? "qba-faq-button-full"
                      : "qba-faq-button-icon-only"
                  }`}
                  data-tour="nav-help"
                >
                  <div
                    className="qba-faq-button-group"
                    onClick={() => {
                      setShowTourPanel(true);
                    }}
                  >
                    <div className="qba-faq-chip">
                      <span className="qba-faq-emoji" aria-hidden="true">
                        🧭
                      </span>
                    </div>
                    {navExpanded ? (
                      <>
                        <div className="qba-faq-button-label">
                          <span className="qba-faq-label-title">
                            {i18n.t("how_can_I_use")}
                          </span>
                        </div>
                        <span className="qba-faq-go" aria-hidden="true">
                          ›
                        </span>
                      </>
                    ) : null}
                  </div>
                </div>
              ) : null}

              {userInformation ? (
                <>
                  <DividerLine />
                  <div className="qba-profile-wrap" ref={profileMenuRef}>
                    {showProfileMenu ? (
                      <div className="qba-profile-menu" role="menu">
                        <button
                          type="button"
                          role="menuitem"
                          className="qba-menu-item"
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/profile");
                          }}
                        >
                          <Icon name="user-profile" size="small" />
                          <span>{i18n.t("my_profile")}</span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="qba-menu-item"
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/");
                            setBoarding("PROFILE_SELECTION_REQUIRED");
                            handleStateChangeProfile();
                            localStorage.setItem("appState", "CHANGE_PROFILE");
                          }}
                        >
                          <Icon name="change-profile" size="small" />
                          <span>{i18n.t("change_profile")}</span>
                        </button>
                        <button
                          type="button"
                          role="menuitem"
                          className="qba-menu-item"
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/support");
                          }}
                        >
                          <Icon name="info" size="small" />
                          <span>{i18n.t("support")}</span>
                        </button>
                        <DividerLine />
                        <button
                          type="button"
                          role="menuitem"
                          className="qba-menu-item qba-menu-item-danger"
                          onClick={() => {
                            setShowProfileMenu(false);
                            navigate("/");
                            handleStateLogout();
                            setBoarding("LANDING");
                            NotificationManager.success(
                              i18n.t("logout_successful_text"),
                            );
                          }}
                        >
                          <Icon name="logout" size="small" />
                          <span>{i18n.t("logout")}</span>
                        </button>
                      </div>
                    ) : null}
                    <button
                      type="button"
                      className={`qba-profile ${
                        navExpanded ? "" : "qba-profile-icon-only"
                      }`}
                      aria-haspopup="menu"
                      aria-expanded={showProfileMenu}
                      title={i18n.t("my_profile")}
                      onClick={() => setShowProfileMenu((open) => !open)}
                    >
                      <div
                        className="qba-avatar-wrap"
                        title={
                          webSocketConnectionStatus === "connected"
                            ? i18n.t("connected")
                            : webSocketConnectionStatus === "connecting"
                              ? i18n.t("connecting")
                              : i18n.t("disconnected")
                        }
                      >
                        <div className="qba-mascot-avatar">
                          <MascotWithItems
                            inventoryItemDTOList={mascotInventoryItemDTOList}
                            mascotState="CLOSED"
                          />
                        </div>
                        <span
                          className={`qba-ws-dot qba-ws-${
                            webSocketConnectionStatus || "disconnected"
                          }`}
                        >
                          {webSocketConnectionStatus === "connected" ||
                          webSocketConnectionStatus === "connecting" ? (
                            <span className="qba-ws-ping anim-ping" />
                          ) : null}
                        </span>
                      </div>
                      {navExpanded ? (
                        <div className="qba-profile-info">
                          <div>
                            <p className="qba-profile-name">
                              {userInformation.firstName}{" "}
                              {userInformation.lastName}
                            </p>
                          </div>
                          {userProfileGrade?.name || userProfileBranch?.name ? (
                            <div>
                              <p className="qba-profile-class">
                                {userProfileGrade?.name}
                                {userProfileGrade?.name &&
                                userProfileBranch?.name
                                  ? " · "
                                  : ""}
                                {userProfileBranch?.name}
                              </p>
                            </div>
                          ) : null}
                        </div>
                      ) : null}
                      {navExpanded ? (
                        <span
                          className={`qba-profile-go ${
                            showProfileMenu ? "qba-profile-go-open" : ""
                          }`}
                          aria-hidden="true"
                        >
                          ›
                        </span>
                      ) : null}
                    </button>
                  </div>
                </>
              ) : null}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default MainLeftNavigation;
